import { motion } from 'framer-motion';
import moment from 'moment-timezone';
import React, { useRef, useState } from 'react';
import Map from '../../centered/components/Map';
import { useResponsive } from '../../../shared/hooks/useResponsive';
import { CustomerInfoType } from '../../../shared/hooks/useCustomerInfo';
import { Service } from '../../../shared/hooks/useServiceSelection';

interface ImmersiveConfirmationPanelsProps {
  selectedServices: Service[];
  selectedDateTime: Date | null;
  selectedTime: string | null;
  customerInfo: CustomerInfoType;
  widgetConfiguration: any;
  location: any | null;
  timeZone: string;
  serviceProviderMap: any;
  nodeData?: any;
  totalPrice: number;
  discount: number;
  discountType: 'discount' | 'fixed' | null;
  couponCode: string;
  setCouponCode: (code: string) => void;
  isApplying: boolean;
  isSubmitting: boolean;
  message: { type: 'success' | 'error'; text: string } | null;
  onPrev: () => void;
  handleConfirm: () => void;
  applyCoupon: () => void;
  formatDiscount: () => string;
  settings: any;
}

function cloudinaryFill(url: string, w: number, h: number): string {
  if (!url) return url;
  const idx = url.indexOf('/upload/');
  if (idx === -1) return url;
  return url.slice(0, idx + 8) + `c_fill,g_auto,w_${w},h_${h}/` + url.slice(idx + 8);
}

const PANEL_W = 960;
const PANEL_H = 1080;

const ImmersiveConfirmationPanels: React.FC<ImmersiveConfirmationPanelsProps> = ({
  selectedServices,
  selectedDateTime,
  selectedTime,
  customerInfo,
  widgetConfiguration,
  location,
  timeZone,
  serviceProviderMap,
  totalPrice,
  discountType,
  couponCode,
  setCouponCode,
  isApplying,
  isSubmitting,
  message,
  onPrev,
  handleConfirm,
  applyCoupon,
  settings,
}) => {
  const currency = widgetConfiguration?.currency?.symbol ?? '$';
  const showCoupon = settings?.showCoupon ?? true;
  const showPrice = settings?.showPrice ?? true;

  const hasProviders = serviceProviderMap && Object.keys(serviceProviderMap).length > 0;
  const hasLocation = location && location.latitude && location.longitude;

  // First provider for display
  const firstService = selectedServices[0];
  const firstProvider = firstService ? serviceProviderMap[firstService.id] : null;
  const providerImageUrl = firstProvider?.image?.secure_url
    ? cloudinaryFill(firstProvider.image.secure_url, PANEL_W, PANEL_H)
    : null;

  const serviceImageUrl = firstService?.image?.secure_url
    ? cloudinaryFill(firstService.image.secure_url, PANEL_W, PANEL_H)
    : null;

  const formattedDate = selectedDateTime
    ? moment(selectedDateTime).format('ddd, D MMM YYYY')
    : '—';
  const formattedTime = selectedTime
    ? moment.tz(`${moment(selectedDateTime).format('YYYY-MM-DD')} ${selectedTime}`, 'YYYY-MM-DD HH:mm', timeZone).format('HH:mm')
    : '—';

  const { isMobile } = useResponsive();

  const multiService = selectedServices.length > 1;
  const slideCount = hasProviders ? selectedServices.length : Math.ceil(selectedServices.length / 2);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showScrollUpIndicator, setShowScrollUpIndicator] = useState(false);
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowScrollIndicator(scrollTop < scrollHeight - clientHeight - 10);
    setShowScrollUpIndicator(scrollTop > 10);
  };

  // panels array is kept only for location/details animation delay
  const panels: string[] = ['service'];
  if (!multiService && hasProviders) panels.push('provider');
  if (hasLocation) panels.push('location');
  panels.push('details');

  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden', background: '#080808', position: 'relative' }}>
        <style>{`#lp-conf-m::-webkit-scrollbar { display: none; }`}</style>


        <div id="lp-conf-m" style={{ height: 'calc(100vh - 72px)', overflowY: 'auto', scrollbarWidth: 'none' }}>

          {/* Service card */}
          <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            {serviceImageUrl
              ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${serviceImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)' }} />}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
            <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Service</p>
              {selectedServices.map(s => (
                <div key={s.id}>
                  <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{s.name}</p>
                  {s.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>{Math.round(s.duration / 60)} min</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Provider card */}
          {hasProviders && (
            <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              {providerImageUrl
                ? <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${providerImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)' }} />}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Provider</p>
                {selectedServices.map(service => {
                  const p = serviceProviderMap[service.id];
                  if (!p) return null;
                  const name = 'autoAssigned' in p ? 'Auto-assigned' : `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
                  return <p key={service.id} style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{name}</p>;
                })}
              </div>
            </div>
          )}

          {/* Location card */}
          {hasLocation && (
            <div style={{ position: 'relative', height: 200, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ position: 'absolute', inset: 0 }}>
                <Map position={{ lat: location.latitude, lng: location.longitude }} onLocationChange={() => {}} disableInteraction zoom={14} showMarker />
              </div>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.78) 100%)' }} />
              <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 2 }}>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>Location</p>
                <p style={{ color: '#fff', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>{location.name}</p>
                {location.address && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginTop: 2 }}>{location.address}</p>}
              </div>
            </div>
          )}

          {/* Details */}
          <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>Your details</p>
            <p style={{ color: '#fff', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {customerInfo.firstName} {customerInfo.lastName}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.32)', fontSize: 13, letterSpacing: '0.04em', lineHeight: 1.7 }}>
              {customerInfo.email}{customerInfo.phone && <><br />{customerInfo.phone}</>}
            </p>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <DetailRow label="Date" value={formattedDate} />
              <DetailRow label="Time" value={`${formattedTime} · ${timeZone}`} />
            </div>
            {showPrice && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Total</span>
                <span style={{ color: '#fff', fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>{currency}{totalPrice.toFixed(2)}</span>
              </div>
            )}
            {showCoupon && widgetConfiguration?.isGiftActivated && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Gift code"
                    style={{ flex: 1, padding: '9px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#fff', fontSize: 13, outline: 'none' }} />
                  <button onClick={applyCoupon} disabled={isApplying || !couponCode}
                    style={{ padding: '9px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: isApplying || !couponCode ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: isApplying || !couponCode ? 'default' : 'pointer' }}>
                    {isApplying ? '...' : 'Apply'}
                  </button>
                </div>
                {message && (
                  <span style={{ fontSize: 11, color: message.type === 'success' ? 'rgba(100,220,120,0.85)' : 'rgba(255,90,90,0.85)' }}>
                    {message.text}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 72, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, zIndex: 10 }}>
          <button onClick={onPrev} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', padding: '9px 12px', flexShrink: 0 }}>
            ← Back
          </button>
          <div style={{ flex: 1 }} />
          <motion.button
            onClick={!isSubmitting ? handleConfirm : undefined}
            whileTap={!isSubmitting ? { scale: 0.97 } : {}}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: isSubmitting ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: `1px solid ${isSubmitting ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.18)'}`, borderRadius: 2, color: isSubmitting ? 'rgba(255,255,255,0.25)' : '#fff', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: isSubmitting ? 'default' : 'pointer', flexShrink: 0 }}
          >
            {isSubmitting ? <SubmittingDots /> : <>Confirm <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)' }}>→</span></>}
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      background: '#080808',
    }}>

      {multiService ? (
        // ── Multiple services: vertically scroll-snapping left section ──
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          style={{ flex: 2, position: 'relative', height: '100vh', flexShrink: 0 }}
        >
          {/* Scroll-down indicator — just above bottom bar */}
          {showScrollIndicator && slideCount > 1 && (
            <div style={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 40,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}>
              <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
                <motion.div
                  animate={{ y: ['-100%', '100%'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
                />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
            </div>
          )}

          {/* Scroll-up indicator — top */}
          {showScrollUpIndicator && slideCount > 1 && (
            <div style={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 40,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase' }}>scroll</p>
              <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.15)', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
                <motion.div
                  animate={{ y: ['100%', '-100%'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.7)', borderRadius: 1 }}
                />
              </div>
            </div>
          )}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              width: '100%',
              height: '100vh',
              overflowY: 'scroll',
              scrollSnapType: 'y mandatory',
              scrollbarWidth: 'none',
            }}
          >
          {hasProviders ? (
            // One slide per service+provider pair
            selectedServices.map((service) => {
              const p = serviceProviderMap[service.id];
              const sImg = service.image?.secure_url ? cloudinaryFill(service.image.secure_url, PANEL_W, PANEL_H) : null;
              const pImg = p?.image?.secure_url ? cloudinaryFill(p.image.secure_url, PANEL_W, PANEL_H) : null;
              const name = p ? ('autoAssigned' in p ? 'Auto-assigned' : `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()) : null;
              return (
                <div key={service.id} style={{ display: 'flex', height: '100vh', flexShrink: 0, scrollSnapAlign: 'start' }}>
                  <Panel imageUrl={sImg} index={0}>
                    <PanelLabel>Service</PanelLabel>
                    <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{service.name}</p>
                    {service.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', marginTop: 4 }}>{Math.round(service.duration / 60)} min</p>}
                  </Panel>
                  <Divider />
                  <Panel imageUrl={pImg} index={1}>
                    <PanelLabel>Provider</PanelLabel>
                    {name && <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{name}</p>}
                  </Panel>
                </div>
              );
            })
          ) : (
            // No providers: pair services side by side per slide
            Array.from({ length: Math.ceil(selectedServices.length / 2) }, (_, slideIdx) => {
              const s1 = selectedServices[slideIdx * 2];
              const s2 = selectedServices[slideIdx * 2 + 1];
              const s1Img = s1?.image?.secure_url ? cloudinaryFill(s1.image.secure_url, PANEL_W, PANEL_H) : null;
              const s2Img = s2?.image?.secure_url ? cloudinaryFill(s2.image.secure_url, PANEL_W, PANEL_H) : null;
              return (
                <div key={slideIdx} style={{ display: 'flex', height: '100vh', flexShrink: 0, scrollSnapAlign: 'start' }}>
                  <Panel imageUrl={s1Img} index={0}>
                    <PanelLabel>Service</PanelLabel>
                    <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{s1.name}</p>
                    {s1.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', marginTop: 4 }}>{Math.round(s1.duration / 60)} min</p>}
                  </Panel>
                  <Divider />
                  {s2 ? (
                    <Panel imageUrl={s2Img} index={1}>
                      <PanelLabel>Service</PanelLabel>
                      <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{s2.name}</p>
                      {s2.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', marginTop: 4 }}>{Math.round(s2.duration / 60)} min</p>}
                    </Panel>
                  ) : (
                    <div style={{ flex: 1, background: 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)' }} />
                  )}
                </div>
              );
            })
          )}
          </div>
        </motion.div>
      ) : (
        // ── Single service: original inline panels ──────────────────────
        <>
          <Panel imageUrl={serviceImageUrl} index={0}>
            <PanelLabel>Service</PanelLabel>
            {selectedServices.map(s => (
              <div key={s.id}>
                <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{s.name}</p>
                {s.duration && <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.06em', marginTop: 4 }}>{Math.round(s.duration / 60)} min</p>}
              </div>
            ))}
          </Panel>
          {hasProviders && (
            <>
              <Divider />
              <Panel imageUrl={providerImageUrl} index={1}>
                <PanelLabel>Provider</PanelLabel>
                {selectedServices.map(service => {
                  const p = serviceProviderMap[service.id];
                  if (!p) return null;
                  const name = 'autoAssigned' in p ? 'Auto-assigned' : `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
                  return (
                    <div key={service.id}>
                      <p style={{ color: '#fff', fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{name}</p>
                    </div>
                  );
                })}
              </Panel>
            </>
          )}
        </>
      )}

      {panels.includes('location') && (
        <>
          <Divider />
          {/* Location panel — map fills the panel, overlay at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: panels.indexOf('location') * 0.08, ease: [0.23, 1, 0.32, 1] }}
            style={{
              flex: 1,
              position: 'relative',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {/* Map fills the full panel */}
            <div style={{ position: 'absolute', inset: 0 }}>
              <Map
                position={{ lat: location.latitude, lng: location.longitude }}
                onLocationChange={() => {}}
                disableInteraction
                zoom={14}
                showMarker
              />
            </div>

            {/* Dark overlay so bottom content is readable */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.85) 100%)',
            }} />

            {/* Bottom content */}
            <div style={{
              position: 'absolute',
              bottom: 88,
              left: 0,
              right: 0,
              padding: '0 28px',
            }}>
              <PanelLabel>Location</PanelLabel>
              <p style={{ color: '#fff', fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                {location.name}
              </p>
              {location.address && (
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, letterSpacing: '0.04em', marginTop: 5 }}>
                  {location.address}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}

      {/* ── DETAILS panel — space-between, date/time at bottom ── */}
      <Divider />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: (panels.length - 1) * 0.08, ease: [0.23, 1, 0.32, 1] }}
        style={{
          flex: 1,
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          background: 'linear-gradient(160deg, #0f0f0f 0%, #121212 100%)',
        }}
      >
        <div style={{
          position: 'absolute',
          top: 52,
          bottom: 96,
          left: 0,
          right: 0,
          padding: '0 28px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Top — label */}
          <PanelLabel>Your details</PanelLabel>

          {/* Middle — big name + contact, truly centered */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <p style={{
              color: '#ffffff',
              fontSize: 'clamp(36px, 3.6vw, 56px)',
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              marginBottom: 16,
              textAlign: 'center',
            }}>
              {customerInfo.firstName}<br />{customerInfo.lastName}
            </p>
            <p style={{
              color: 'rgba(255,255,255,0.32)',
              fontSize: 13,
              letterSpacing: '0.04em',
              lineHeight: 1.7,
              textAlign: 'center',
            }}>
              {customerInfo.email}
              {customerInfo.phone && <><br />{customerInfo.phone}</>}
            </p>
          </div>

          {/* Bottom — date & time */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 4 }} />
            <DetailRow label="Date" value={formattedDate} />
            <DetailRow label="Time" value={`${formattedTime} · ${timeZone}`} />
          </div>
        </div>
      </motion.div>

      {/* ── Bottom floating bar ─────────────────────────────────── */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 80,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 48px',
        gap: 24,
        zIndex: 10,
      }}>

        {/* Gift code — left */}
        {showCoupon && widgetConfiguration?.isGiftActivated && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <input
              value={couponCode}
              onChange={e => setCouponCode(e.target.value)}
              placeholder="Gift code"
              style={{
                width: 160,
                padding: '9px 14px',
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6,
                color: '#fff',
                fontSize: 13,
                letterSpacing: '0.04em',
                outline: 'none',
              }}
            />
            <button
              onClick={applyCoupon}
              disabled={isApplying || !couponCode}
              style={{
                padding: '9px 14px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6,
                color: isApplying || !couponCode ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)',
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                cursor: isApplying || !couponCode ? 'default' : 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {isApplying ? '...' : 'Apply'}
            </button>
            {message && (
              <span style={{
                fontSize: 11,
                letterSpacing: '0.04em',
                color: message.type === 'success' ? 'rgba(100,220,120,0.85)' : 'rgba(255,90,90,0.85)',
              }}>
                {message.text}
              </span>
            )}
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Total — center-right */}
        {showPrice && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexShrink: 0 }}>
            {discountType && (
              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textDecoration: 'line-through' }}>
                {currency}{selectedServices.reduce((s, x) => s + x.price, 0).toFixed(2)}
              </span>
            )}
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              Total
            </span>
            <span style={{ color: '#fff', fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' }}>
              {currency}{totalPrice.toFixed(2)}
            </span>
          </div>
        )}

        {/* Back */}
        <button
          onClick={onPrev}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.25)',
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: '9px 16px',
            flexShrink: 0,
          }}
        >
          ← Back
        </button>

        {/* Confirm */}
        <motion.button
          onClick={!isSubmitting ? handleConfirm : undefined}
          whileHover={!isSubmitting ? { background: 'rgba(255,255,255,0.14)', scale: 1.02 } : {}}
          whileTap={!isSubmitting ? { scale: 0.98 } : {}}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 28px',
            background: isSubmitting ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${isSubmitting ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.18)'}`,
            borderRadius: 2,
            color: isSubmitting ? 'rgba(255,255,255,0.25)' : '#fff',
            fontSize: 13,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            cursor: isSubmitting ? 'default' : 'pointer',
            transition: 'all 0.25s ease',
            flexShrink: 0,
          }}
        >
          {isSubmitting ? (
            <SubmittingDots />
          ) : (
            <>
              Confirm booking
              <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.45)' }}>→</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

// ── Panel ────────────────────────────────────────────────────────────────────

const Panel: React.FC<{
  imageUrl: string | null;
  index: number;
  children: React.ReactNode;
}> = ({ imageUrl, index, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: [0.23, 1, 0.32, 1] }}
    style={{
      flex: 1,
      position: 'relative',
      height: '100%',
      overflow: 'hidden',
    }}
  >
    {/* Background image or dark surface */}
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: imageUrl
        ? `url(${imageUrl})`
        : 'linear-gradient(160deg, #0f0f0f 0%, #141414 100%)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }} />

    {/* Gradient overlay — bottom-heavy so text is always readable */}
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.92) 100%)',
    }} />

    {/* Content */}
    <div style={{
      position: 'absolute',
      bottom: 88,
      left: 0,
      right: 0,
      padding: '0 28px',
    }}>
      {children}
    </div>
  </motion.div>
);

const Divider: React.FC = () => (
  <div style={{ width: 1, flexShrink: 0, background: 'rgba(255,255,255,0.07)', zIndex: 2 }} />
);

const PanelLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{
    color: 'rgba(255,255,255,0.3)',
    fontSize: 10,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    marginBottom: 12,
  }}>
    {children}
  </p>
);

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 2 }}>
      {label}
    </p>
    <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, letterSpacing: '0.02em', lineHeight: 1.4 }}>
      {value}
    </p>
  </div>
);

const SubmittingDots: React.FC = () => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    {[0, 1, 2].map(i => (
      <motion.div
        key={i}
        animate={{ opacity: [0.15, 0.5, 0.15] }}
        transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
        style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }}
      />
    ))}
  </div>
);

export default ImmersiveConfirmationPanels;
